import { Coordinates } from "./types";
import { GOOGLE_MAPS_CONFIG, getStateBoundsForSearch } from "@/config/maps";
import {
  GoogleMapsDisabledError,
  GoogleMapsNetworkError,
  GoogleMapsInvalidResponseError,
  createGoogleMapsError,
  isGoogleMapsError,
} from "../types/googleMapsErrors";
import logger from "./utils/logger";
import { geocodeCache } from "./geocodeCache";

export interface GoogleMapsLocation {
  placeId: string;
  name: string;
  formattedAddress: string;
  coordinates: Coordinates;
  types: string[];
}

export interface GoogleMapsSearchResult {
  locations: GoogleMapsLocation[];
  status: string;
}

// Client-side settings cache for efficient access
let clientSettingsCache: {
  google_maps_enabled: boolean;
  expiry: number;
} | null = null;
const CLIENT_CACHE_DURATION = 60 * 1000; // 1 minute

/**
 * Get Google Maps enabled status from client-side cache or fetch if needed
 */
async function getClientGoogleMapsStatus(): Promise<boolean> {
  const now = Date.now();

  // Return cached status if still valid
  if (clientSettingsCache && now < clientSettingsCache.expiry) {
    return clientSettingsCache.google_maps_enabled;
  }

  try {
    const response = await fetch("/api/settings/system", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      logger.warn(
        "[GMAPS_UTILS] Failed to fetch settings, defaulting to enabled",
      );
      return true; // Default to enabled if we can't fetch settings
    }

    const data = await response.json();
    const isEnabled = data.settings?.google_maps_enabled ?? true;

    // Cache the result
    clientSettingsCache = {
      google_maps_enabled: isEnabled,
      expiry: now + CLIENT_CACHE_DURATION,
    };

    return isEnabled;
  } catch (error) {
    logger.warn("[GMAPS_UTILS] Error fetching settings:", error);
    return true; // Default to enabled on error
  }
}

/**
 * Enhanced fetch wrapper for Google Maps API calls with better error handling
 */
async function fetchGoogleMapsAPI(
  url: string,
  options: RequestInit,
  operationName: string,
): Promise<Record<string, unknown>> {
  try {
    // Check if Google Maps is enabled client-side first
    const isEnabled = await getClientGoogleMapsStatus();
    if (!isEnabled) {
      logger.log(
        `[GMAPS_UTILS] ${operationName}: Google Maps disabled client-side, skipping API call`,
      );
      throw new GoogleMapsDisabledError(
        "Google Maps services are disabled in system settings",
      );
    }

    logger.log(`[GMAPS_UTILS] ${operationName}: Making API call`);

    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    });

    let responseData;
    try {
      responseData = await response.json();
    } catch (parseError) {
      logger.error(
        `[GMAPS_UTILS] ${operationName}: Failed to parse response JSON:`,
        parseError,
      );
      throw new GoogleMapsInvalidResponseError(
        "Invalid JSON response from server",
      );
    }

    if (!response.ok) {
      logger.error(`[GMAPS_UTILS] ${operationName}: API error:`, {
        status: response.status,
        data: responseData,
      });
      throw createGoogleMapsError(response, responseData);
    }

    logger.log(`[GMAPS_UTILS] ${operationName}: Success`);
    return responseData;
  } catch (error) {
    // If it's already a GoogleMapsError, just re-throw it
    if (isGoogleMapsError(error)) {
      throw error;
    }

    // Handle network errors
    if (error instanceof TypeError && error.message.includes("fetch")) {
      logger.error(`[GMAPS_UTILS] ${operationName}: Network error:`, error);
      throw new GoogleMapsNetworkError(`Network error during ${operationName}`);
    }

    // Handle other unexpected errors
    logger.error(`[GMAPS_UTILS] ${operationName}: Unexpected error:`, error);
    throw new GoogleMapsInvalidResponseError(
      `Unexpected error during ${operationName}`,
      error instanceof Error ? error.message : String(error),
    );
  }
}

/**
 * Extract the area/neighborhood name from a location
 * This is used to convert street-level locations to area-level
 */
const extractAreaName = (location: GoogleMapsLocation): string | null => {
  // Look for neighborhood or sublocality in address components
  const addressParts = location.formattedAddress
    .split(",")
    .map((part) => part.trim());

  // If it's already an area-level location, return the name as is
  if (
    location.types.some((type) =>
      GOOGLE_MAPS_CONFIG.SECURITY_CONFIG.ALLOWED_STORAGE_TYPES.includes(type),
    )
  ) {
    return location.name;
  }

  // For street addresses, try to extract the neighborhood/area
  // Usually the area name comes after the street name in the address
  if (addressParts.length > 1) {
    return addressParts[1];
  }

  return null;
};

/**
 * SECURITY: Validate that a location can be searched
 * This is more permissive than storage validation
 */
export const validateLocationForSearch = (
  location: GoogleMapsLocation,
): boolean => {
  const { EXCLUDED_TYPES, ALLOWED_STORAGE_TYPES, SEARCHABLE_TYPES } =
    GOOGLE_MAPS_CONFIG.SECURITY_CONFIG;

  // Check if location contains any completely excluded types
  const hasExcludedTypes = location.types.some((type) =>
    EXCLUDED_TYPES.includes(type),
  );

  if (hasExcludedTypes) {
    logger.log("[GMAPS_UTILS] Location filtered: contains excluded types", {
      location,
      excludedTypes: EXCLUDED_TYPES.filter((type) =>
        location.types.includes(type),
      ).join(", "),
    });
    return false;
  }

  // For search, we want to allow both storable and searchable types
  const hasValidType = location.types.some(
    (type) =>
      ALLOWED_STORAGE_TYPES.includes(type) || SEARCHABLE_TYPES.includes(type),
  );

  // Additional check for route/street names
  const isStreetLevel = location.types.some((type) =>
    ["route", "street_address"].includes(type),
  );

  // Allow if it's either a valid area type or a street-level result
  if (hasValidType || isStreetLevel) {
    logger.log("[GMAPS_UTILS] Location allowed:", {
      location,
      types: location.types.join(", "),
    });
    return true;
  }

  logger.log("[GMAPS_UTILS] Location filtered: no valid types found", {
    location,
    types: location.types.join(", "),
  });
  return false;
};

/**
 * SECURITY: Validate that a location meets our storage requirements
 * This is stricter than search validation
 */
export const validateLocationForStorage = (
  location: GoogleMapsLocation,
): {
  isValid: boolean;
  areaName?: string;
  message?: string;
} => {
  const { ALLOWED_STORAGE_TYPES, MESSAGES } =
    GOOGLE_MAPS_CONFIG.SECURITY_CONFIG;

  // If it's already an area-level location, validate it
  if (location.types.some((type) => ALLOWED_STORAGE_TYPES.includes(type))) {
    return {
      isValid: true,
      areaName: location.name,
      message: MESSAGES.PRIVACY_NOTICE,
    };
  }

  // For street-level locations, try to extract the area name
  const areaName = extractAreaName(location);
  if (!areaName) {
    return {
      isValid: false,
      message:
        "Could not determine the neighborhood for this location. Please select an area instead.",
    };
  }

  return {
    isValid: true,
    areaName,
    message: MESSAGES.LOCATION_SELECTED(location.name, areaName),
  };
};

/**
 * SECURITY: Filter location results to only include searchable locations
 */
export const filterSearchableLocations = (
  locations: GoogleMapsLocation[],
): GoogleMapsLocation[] => {
  return locations.filter(validateLocationForSearch);
};

/**
 * Search for locations using Google Maps Places API
 */
export const searchGoogleMapsLocations = async (
  query: string,
  center?: Coordinates,
  radius?: number,
  stateName?: string,
): Promise<GoogleMapsSearchResult> => {
  try {
    // Use state-specific center and bounds if state is provided
    let searchCenter = center || GOOGLE_MAPS_CONFIG.DEFAULT_CENTER;
    let searchBounds = undefined;

    if (stateName) {
      const stateBounds = await getStateBoundsForSearch(stateName);
      if (stateBounds) {
        searchCenter = stateBounds.center;
        searchBounds = {
          north: stateBounds.north,
          south: stateBounds.south,
          west: stateBounds.west,
          east: stateBounds.east,
        };
      }
    }

    const result = await fetchGoogleMapsAPI(
      "/api/maps/search",
      {
        method: "POST",
        body: JSON.stringify({
          query,
          location: searchCenter,
          radius: Math.max(
            radius || GOOGLE_MAPS_CONFIG.DEFAULT_RADIUS,
            GOOGLE_MAPS_CONFIG.SECURITY_CONFIG.MIN_SEARCH_RADIUS,
          ),
          bounds: searchBounds,
          stateName: stateName,
        }),
      },
      "searchGoogleMapsLocations",
    );

    // Check if Google Maps is disabled
    if (result.status === "DISABLED") {
      logger.log(
        "[GMAPS_UTILS] Google Maps is disabled, returning empty results",
      );
      return {
        locations: [],
        status: "DISABLED",
      };
    }

    // SECURITY: Filter results to ensure only neighborhood-level locations
    const locations = Array.isArray(result.locations) ? result.locations : [];
    const secureLocations = filterSearchableLocations(
      locations as GoogleMapsLocation[],
    );

    return {
      locations: secureLocations,
      status: typeof result.status === "string" ? result.status : "ERROR",
    };
  } catch (error) {
    if (isGoogleMapsError(error)) {
      logger.log(
        `[GMAPS_UTILS] searchGoogleMapsLocations: ${error.type} - ${error.message}`,
      );

      if (error.type === "DISABLED") {
        return {
          locations: [],
          status: "DISABLED",
        };
      }
    } else {
      logger.error(
        "[GMAPS_UTILS] searchGoogleMapsLocations: Unexpected error:",
        error,
      );
    }

    return {
      locations: [],
      status: "ERROR",
    };
  }
};

/**
 * Get place details from Google Maps Places API
 */
export const getGoogleMapsPlaceDetails = async (
  placeId: string,
): Promise<GoogleMapsLocation | null> => {
  try {
    const result = await fetchGoogleMapsAPI(
      "/api/maps/place-details",
      {
        method: "POST",
        body: JSON.stringify({
          placeId,
        }),
      },
      "getGoogleMapsPlaceDetails",
    );

    // Type guard to ensure result is a GoogleMapsLocation
    if (
      result &&
      typeof result === "object" &&
      "placeId" in result &&
      "name" in result &&
      "formattedAddress" in result &&
      "coordinates" in result &&
      "types" in result
    ) {
      return result as unknown as GoogleMapsLocation;
    }

    return null;
  } catch (error) {
    if (isGoogleMapsError(error)) {
      logger.log(
        `[GMAPS_UTILS] getGoogleMapsPlaceDetails: ${error.type} - ${error.message}`,
      );
    } else {
      logger.error(
        "[GMAPS_UTILS] getGoogleMapsPlaceDetails: Unexpected error:",
        error,
      );
    }
    return null;
  }
};

/**
 * Geocode an address to coordinates
 */
export const geocodeAddress = async (
  address: string,
): Promise<Coordinates | null> => {
  try {
    // Use cache for deduplication and caching
    const cached = await geocodeCache.geocode(address);
    if (cached) {
      return cached.coordinates;
    }
    return null;
  } catch (error) {
    if (isGoogleMapsError(error)) {
      logger.log(
        `[GMAPS_UTILS] geocodeAddress: ${error.type} - ${error.message}`,
      );
    } else {
      logger.error("[GMAPS_UTILS] geocodeAddress: Unexpected error:", error);
    }
    return null;
  }
};

/**
 * Reverse geocode coordinates to address
 * Uses cache for deduplication and caching
 */
export const reverseGeocode = async (
  coordinates: Coordinates,
): Promise<string | null> => {
  try {
    // Use cache for deduplication and caching
    return await geocodeCache.reverseGeocode(coordinates);
  } catch (error) {
    if (isGoogleMapsError(error)) {
      logger.log(
        `[GMAPS_UTILS] reverseGeocode: ${error.type} - ${error.message}`,
      );
    } else {
      logger.error("[GMAPS_UTILS] reverseGeocode: Unexpected error:", error);
    }
    return null;
  }
};
